import React, { useState } from "react";
import Popup from "src/Components/Popup/popup";
import Form from "src/Components/Form";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { setIndividualCharge as addIndividualChargeApi } from "src/Pages/Company/api/rechargePlanningSystem/clients";
import { cyclicChargesPermissionWrite } from "src/utils/RoleBasedSecurity/permissions";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { CyclicChargeCandidate } from "src/Pages/Company/api/rechargePlanningSystem/rechargePlanningSystem.models";
import { ChargeUpsRow } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/ChargeUps/ChargeUps.models";

type CyclicChargeModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (cyclicChargeCandidate: CyclicChargeCandidate | null) => void;
  account: ChargeUpsRow;
  employeeId: string;
}>;

export default function CyclicChargeModal({
  isOpen,
  onClose,
  onSuccess,
  account,
  employeeId,
}: CyclicChargeModalProps) {
  const existingIndividualAmount =
    account.cyclicChargeCandidate?.chargeCause === "INDIVIDUAL"
      ? String(account.cyclicChargeCandidate.amount)
      : null;
  const [amount, setAmount] = useState<string | null>(existingIndividualAmount);
  const hasCyclicChargeWritePermission = usePermission(cyclicChargesPermissionWrite);

  const submit = async () => {
    if (!account.cyclicChargeDefinitionId) {
      dynamicNotification(__("Brak skonfigurowanego doładowania cyklicznego"), "error");
      return;
    }
    try {
      const updatedCandidate = await addIndividualChargeApi(
        account.cyclicChargeDefinitionId,
        account.accountOwnerId,
        account.pointsBank.id,
        Number.parseFloat(amount!)
      );
      dynamicNotification(__("Zmieniono doładowanie cykliczne"), "success");
      onSuccess(updatedCandidate);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      newRelicErrorReport(e, "ChargeUps/modals/CyclicChargeModal.tsx");
      dynamicNotification(
        __("Nie można ustawić cyklicznego doładowania"),
        "error"
      );
    }
  };

  return (
    <Popup
      id="cyclicChargeModal"
      isOpen={isOpen}
      toggle={onClose}
      unmountOnClose
      size="md"
    >
      <Form
        id="cyclicChargeForm"
        data={{ amount }}
        config={{
          isInPopup: true,
          togglePopup: onClose,
          title: __("Ustaw indywidualne doładowanie cykliczne"),
          onSubmit: submit,
          defaultOnChange: (_key: string, value: string | null) =>
            setAmount(value),
          buttons: [
            { onClick: onClose, text: __("Zamknij"), color: "light" },
            {
              text: __("Wykonaj"),
              type: "submit",
              disabled: !hasCyclicChargeWritePermission,
            },
          ],
          noCards: true,
          formGroups: [
            {
              formElements: [
                {
                  id: "amount",
                  dataId: "amount",
                  label: __("Wartość doładowania"),
                  type: "text",
                  valueFormatter: "price",
                  validation: [
                    "requiredNumericField",
                    { method: "greaterEqualThan", args: [0] },
                  ],
                },
              ],
            },
          ],
        }}
      />
    </Popup>
  );
}
